import { addDays, durationParts, type LocalDate, type QuitPhase } from '@tahi/core';
import { copy } from './copy';

const WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];
const unit = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The toast after a +1 (brand.md: "That's 11 today, one over your limit. Logged. Tomorrow's limit is still 10."). */
export function logToast(r: {
  count: number;
  limit: number | null;
  tomorrowLimit: number | null;
  phase: QuitPhase;
}): string {
  if (r.phase === 'smoke_free') return `Logged. ${r.count} today. Your plan carries on.`;
  if (r.limit === null) return `Logged. ${r.count} today.`;
  if (r.count < r.limit) return `Logged. ${r.count} today, limit ${r.limit}.`;
  if (r.count === r.limit) return `Logged. That's ${r.count} today, right at your limit.`;
  const over = r.count - r.limit;
  const tomorrow =
    r.tomorrowLimit === null
      ? ''
      : r.tomorrowLimit === r.limit
        ? ` Tomorrow's limit is still ${r.tomorrowLimit}.`
        : ` Tomorrow's limit is ${r.tomorrowLimit}.`;
  return `That's ${r.count} today, ${WORDS[over] ?? String(over)} over your limit. Logged.${tomorrow}`;
}

/** "2 hours, 15 minutes"; the two largest parts only. */
export function durationText(minutes: number): string {
  const { days, hours, minutes: mins } = durationParts(minutes);
  const parts: string[] = [];
  if (days > 0) parts.push(unit(days, 'day', 'days'));
  if (hours > 0) parts.push(unit(hours, 'hour', 'hours'));
  if (days === 0 && mins > 0) parts.push(unit(mins, 'minute', 'minutes'));
  return parts.length === 0 ? 'under a minute' : parts.slice(0, 2).join(', ');
}

const dateFmt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...opts });

/** "Today", "Yesterday", or "Fri, Oct 30". */
export function dayLabel(date: LocalDate, today: LocalDate): string {
  if (date === today) return 'Today';
  if (date === addDays(today, -1)) return 'Yesterday';
  return dateFmt({ weekday: 'short', month: 'short', day: 'numeric' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

/** "Jan 11, 2027". */
export function shortDate(date: LocalDate): string {
  return dateFmt({ month: 'short', day: 'numeric', year: 'numeric' }).format(
    new Date(`${date}T00:00:00Z`),
  );
}

/** "Mon" for chart labels. */
export function weekdayShort(date: LocalDate): string {
  return dateFmt({ weekday: 'short' }).format(new Date(`${date}T00:00:00Z`));
}

/** "9:10 PM" in the zone the log was recorded in. */
export function timeOfDay(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** "180" -> 18000, "8.5" -> 850; null when empty, zero or malformed. */
export function pesosToMinor(text: string): number | null {
  const t = text.trim().replace(/,/g, '');
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(t)) return null;
  const minor = Math.round(Number(t) * 100);
  return minor > 0 ? minor : null;
}

export function minorToPesos(minor: number): string {
  return minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2);
}

export function countLine(count: number, limit: number | null): string {
  return limit === null ? String(count) : `${count} of ${limit}`;
}

export function spokenCount(count: number, limit: number | null): string {
  return `${copy.cigarettesToday}, ${countLine(count, limit)}`;
}

/** Under the big count: how today stands against the limit, said plainly (brand.md). */
export function statusLine(o: {
  count: number;
  limit: number | null;
  phase: QuitPhase;
}): string | null {
  if (o.phase === 'smoke_free') return o.count === 0 ? copy.smokeFreeToday : null;
  if (o.limit === null) return null;
  if (o.count < o.limit) return copy.leftToday(o.limit - o.count);
  if (o.count === o.limit) return copy.atLimit;
  return copy.overBy(o.count - o.limit);
}
